import ApiService from '../../services/api.service.js';
import AlertServie from '@/services/errors.service';

export const state = {
  reservations: [],
  dates: []
};

export const mutations = {
  SET_RESERVATIONS(state, newValue) {
    state.reservations = newValue;
  },
  SET_DATES(state, newValue) {
    state.dates = newValue;
  }
};

export const getters = {
  getReservations(state) {
    return state.reservations;
  },
  getDates(state) {
    return state.dates;
  }
};

export const actions = {
  async getReservations({ commit, rootGetters }, payload) {
    try {
      if (payload.type === 'room') {
        const resolvedMonth = payload.date || getMonthFilter(new Date());
        const months = Array.isArray(payload.months) && payload.months.length ? payload.months : [resolvedMonth];
        const responses = await Promise.all(months.map((month) => fetchRoomBookingsForMonth(month, payload)));
        let data = responses.flat();

        if (payload.myReservations) {
          const userId = rootGetters['auth/getUserId'];
          data = (data || []).filter((item) => {
            const booking = item?.booking || item;
            const createdById =
              booking?.created_by_id ||
              booking?.createdBy?.id ||
              booking?.booked_by_id ||
              booking?.user_id ||
              item?.created_by_id ||
              item?.createdBy?.id ||
              item?.booked_by_id ||
              item?.user_id;
            return createdById === userId;
          });
        }

        const mappedEvents = data.flatMap((item) => mapRoomBookingToEvents(item));
        const dedupedEvents = dedupeRoomEvents(mappedEvents);
        commit('SET_RESERVATIONS', dedupedEvents);
        return;
      }

      if (payload.type === 'car') {
        const resolvedMonth = payload.date || getMonthFilter(new Date());
        const months = Array.isArray(payload.months) && payload.months.length ? payload.months : [resolvedMonth];
        const responses = await Promise.all(months.map((month) => fetchCarBookingsForMonth(month, payload)));
        let data = responses.flat();

        if (payload.myReservations) {
          const userId = rootGetters['auth/getUserId'];
          data = (data || []).filter((item) => {
            const booking = item?.booking || item;
            const createdById =
              booking?.created_by_id ||
              booking?.createdBy?.id ||
              booking?.booked_by_id ||
              booking?.user_id ||
              item?.created_by_id ||
              item?.createdBy?.id ||
              item?.booked_by_id ||
              item?.user_id;
            return createdById === userId;
          });
        }

        const mappedEvents = data.flatMap((item) => mapCarBookingToEvents(item));
        const dedupedEvents = dedupeRoomEvents(mappedEvents);
        commit('SET_RESERVATIONS', dedupedEvents);
        return;
      }
    } catch (error) {}
  },

  async checkAvailableRoomDates({ commit }, payload) {
    try {
      if (payload.start_date != '' && payload.start_date != undefined) {
        const queryParams = new URLSearchParams();
        if (payload.room > 0) {
          queryParams.append('room_id', payload.room);
        }
        queryParams.append('start_date', prepareDateFormat(payload.start_date));
        queryParams.append('end_date', prepareDateFormat(payload.end_date || payload.start_date));
        if (payload.duration_minutes) {
          queryParams.append('duration_minutes', payload.duration_minutes);
        }
        if (payload.exclude_booking_id) {
          queryParams.append('exclude_booking_id', payload.exclude_booking_id);
        }

        const response = await ApiService.get(`/room-bookings/availability?${queryParams.toString()}`);
        if (response.status === 200) {
          const data = response.data || {};
          const targetDate = prepareDateFormat(payload.start_date);
          const days = Array.isArray(data.days) ? data.days : [];
          const matchedDay = days.find((day) => day.date === targetDate) || days[0];
          const slots = matchedDay?.slots || [];
          const timeSlots = slots.map((slot) => ({
            name: `${slot.start_time} - ${slot.end_time}`,
            value: slot.start_time,
            end_time: slot.end_time,
            available: Boolean(slot.available),
            conflict: slot.conflict,
            booked_by: slot.conflict?.booked_by || null
          }));

          commit('SET_DATES', { ...data, timeSlots });
        }
      }
    } catch (error) {}
  },

  async checkAvailaveCarDates({ commit }, payload) {
    try {
      if (payload.start_date && payload.start_date !== '') {
        const carType = payload.car_type ? `&car_type=${payload.car_type}` : '';
        const endDate = payload.end_date ? prepareDateFormat(payload.end_date) : prepareDateFormat(payload.start_date);
        const response = await ApiService.get(
          `/reservations/check-date?start_date=${payload.start_date}&end_date=${endDate}${carType}&bookable_type=car`
        );
        if (response.status === 200) {
          const data = response.data || {};
          const timeSlots =
            Array.isArray(data.timeSlots) && data.timeSlots.length ? data.timeSlots : normalizeAvailabilitySlots(data.days || []);
          commit('SET_DATES', { ...data, timeSlots });
        }
      }
    } catch (error) {}
  },

  async saveEvent(_, payload) {
    return new Promise(async (resolve, reject) => {
      try {
        if (payload.type === 'room') {
          const roomPayload = normalizeRoomPayload(payload);
          const response = await ApiService.post('/room-bookings', roomPayload);
          if (response.status === 201) {
            AlertServie.successMessage('The event has been successfully added.');
            resolve(true);
          } else {
            resolve(false);
          }
          return;
        }

        if (payload.type === 'car') {
          const carPayload = normalizeCarPayload(payload);
          const response = await ApiService.post('/car-bookings', carPayload);
          if (response.status === 201) {
            AlertServie.successMessage('The event has been successfully added.');
            resolve(true);
          } else {
            resolve(false);
          }
          return;
        }

        resolve(false);
      } catch (error) {
        AlertServie.errorMessage(extractErrorMessage(error));
        resolve(false);
      }
    });
  },

  async editEvent(_, payload) {
    return new Promise(async (resolve, reject) => {
      try {
        if (payload.type === 'room') {
          const roomPayload = normalizeRoomPayload(payload);
          const response = await ApiService.put('/room-bookings/' + payload.id, roomPayload);
          if (response.status === 200) {
            AlertServie.successMessage('The event has been successfully edited.');
            resolve(true);
          } else {
            resolve(false);
          }
          return;
        }

        if (payload.type === 'car') {
          const carPayload = normalizeCarPayload(payload);
          const response = await ApiService.put('/car-bookings/' + payload.id, carPayload);
          if (response.status === 200) {
            AlertServie.successMessage('The event has been successfully edited.');
            resolve(true);
          } else {
            resolve(false);
          }
          return;
        }
      } catch (error) {
        AlertServie.errorMessage(extractErrorMessage(error));
        resolve(false);
      }
    });
  },
  async deleteEvent(_, payload) {
    try {
      if (typeof payload === 'object' && payload !== null && payload.type === 'room') {
        const response = await ApiService.delete('/room-bookings/' + payload.id);
        if (response.status === 200) {
          AlertServie.successMessage('The event has been successfully deleted.');
          // view handles refetch
        }
        return;
      }

      if (typeof payload === 'object' && payload !== null && payload.type === 'car') {
        const response = await ApiService.delete('/car-bookings/' + payload.id);
        if (response.status === 200) {
          AlertServie.successMessage('The event has been successfully deleted.');
          // view handles refetch
        }
        return;
      }
    } catch (error) {}
  },

  /**
   * Approves a car reservation by sending a request to the API.
   * @param {Object} _ - Vuex context (not used).
   * @param {Object} payload - The payload containing reservation details.
   * @param {number} payload.id - The ID of the car booking to approve.
   * @param {number} payload.car_id - The ID of the car.
   * @param {number} payload.driver_id - The ID of the driver.
   */
  async approveReservation(_, payload) {
    try {
      const response = await ApiService.put('/car-bookings/' + payload.id + '/approve', {
        car_id: payload.car_id,
        driver_id: payload.driver_id,
        note: payload.note
      });
      if (response.status === 200) {
        AlertServie.successMessage('The event has been successfully approved.');

        // view handles refetch
      }
    } catch (error) {
      AlertServie.errorMessage(extractErrorMessage(error));
    }
  },

  async rejectReservation(_, payload) {
    try {
      const response = await ApiService.put('/car-bookings/' + payload.id + '/reject', {
        note: payload.note
      });
      if (response.status === 200) {
        AlertServie.successMessage('The event has been successfully rejected.');

        // view handles refetch
      }
    } catch (error) {
      AlertServie.errorMessage(extractErrorMessage(error));
    }
  }
};

function prepareDateFormat(date) {
  const dateStr = date;
  const formattedDateStr = dateStr && dateStr?.split('T')[0];
  return formattedDateStr;
}

function normalizeAvailabilitySlots(days) {
  if (!Array.isArray(days) || days.length === 0) {
    return [];
  }

  const slots = days[0]?.slots || [];
  return slots
    .filter((slot) => slot.available === undefined || slot.available)
    .map((slot) => ({
      name: `${slot.start_time} - ${slot.end_time}`,
      value: slot.start_time,
      end_time: slot.end_time,
      available: slot.available,
      conflict: slot.conflict
    }));
}

async function fetchRoomBookingsForMonth(month, payload) {
  const queryParams = new URLSearchParams();
  if (month) {
    queryParams.append('date', month);
  }
  if (payload.room > 0) {
    queryParams.append('room_id', payload.room);
  }
  if (payload.withTrashed !== undefined) {
    queryParams.append('with_trashed', payload.withTrashed ? 1 : 0);
  }

  const response = await ApiService.get(`/room-bookings?${queryParams.toString()}`);
  if (response.status === 200) {
    return response.data.data || [];
  }
  return [];
}

async function fetchCarBookingsForMonth(month, payload) {
  const queryParams = new URLSearchParams();
  if (month) {
    queryParams.append('date', month);
  }
  if (payload.car_type) {
    queryParams.append('car_type', payload.car_type);
  }
  if (payload.status) {
    queryParams.append('status', payload.status);
  }
  if (payload.withTrashed !== undefined) {
    queryParams.append('with_trashed', payload.withTrashed ? 1 : 0);
  }

  const response = await ApiService.get(`/car-bookings?${queryParams.toString()}`);
  if (response.status === 200) {
    return response.data.data || [];
  }
  return [];
}

function mapRoomBookingToEvents(booking) {
  if (!booking) {
    return [];
  }

  const occurrences = Array.isArray(booking.occurrences) ? booking.occurrences : null;
  if (occurrences && occurrences.length) {
    return dedupeOccurrencesByDate(occurrences).map((occurrence) => buildRoomEvent(booking, occurrence));
  }

  return buildRoomEventsForOccurrence(booking);
}

function mapCarBookingToEvents(booking) {
  if (!booking) {
    return [];
  }

  // Only use occurrences for repeating bookings
  if (booking.repeat_type && Array.isArray(booking.occurrences) && booking.occurrences.length) {
    return dedupeOccurrencesByDate(booking.occurrences).map((occurrence) => buildCarEvent(booking, occurrence));
  }

  // For non-repeating bookings, always create a single event
  return buildCarEventsForOccurrence(booking);
}

function buildRoomEventsForOccurrence(booking, occurrence = {}) {
  if (!booking.repeat_type) {
    return [buildRoomEvent(booking, occurrence)];
  }

  const startAt = occurrence.start_at || booking.start_at || combineDateTime(booking.start_date, booking.start_time);
  const endAt = occurrence.end_at || booking.end_at || combineDateTime(booking.end_date, booking.end_time);
  const startDate = extractDate(startAt) || booking.start_date;
  const endDate = extractDate(endAt) || booking.end_date;
  const startTime = booking.start_time || extractTime(startAt);
  const endTime = booking.end_time || extractTime(endAt);

  const dateSpan = expandDateRange(startDate, endDate);
  if (dateSpan.length > 1) {
    return dateSpan.map((date) => buildRoomEvent(booking, occurrence, date));
  }

  return [buildRoomEvent(booking, occurrence)];
}

function buildCarEventsForOccurrence(booking, occurrence = {}) {
  // If this booking repeats, occurrences should represent single-day instances
  if (booking.repeat_type) {
    return [buildCarEvent(booking, occurrence)];
  }

  // For non-repeating bookings, always return a single event spanning the full date range
  const startAt = occurrence.start_at || booking.start_at || combineDateTime(booking.start_date, booking.start_time);
  const endAt = occurrence.end_at || booking.end_at || combineDateTime(booking.end_date, booking.end_time);
  return [buildCarEvent({ ...booking, start_at: startAt, end_at: endAt }, occurrence)];
}

function buildRoomEvent(booking, occurrence = {}, dateOverride = '') {
  const startAt = occurrence.start_at || booking.start_at || combineDateTime(booking.start_date, booking.start_time);
  const endAt = occurrence.end_at || booking.end_at || combineDateTime(booking.end_date, booking.end_time);
  const startDate = dateOverride || extractDate(startAt) || booking.start_date;
  const endDate = dateOverride || extractDate(endAt) || booking.end_date;
  const startTime = booking.start_time || extractTime(startAt);
  const endTime = booking.end_time || extractTime(endAt);
  const roomName = booking.room?.name || booking.bookable?.name || booking.name;
  const roomId = booking.room_id || booking.bookable_id || booking.room?.id || booking.bookable?.id;

  const bookingPayload = {
    ...booking,
    start: startDate,
    end: endDate,
    start_at_slot: startTime,
    end_at_slot: endTime,
    bookable_id: roomId,
    bookable: booking.room ? { ...booking.room } : booking.bookable,
    name: roomName
  };

  return {
    id: occurrence.id ? `${occurrence.id}-${startDate}` : `${booking.id}-${startDate}`,
    title: booking.title || roomName || 'Booking',
    start: toIsoDateTime(dateOverride ? combineDateTime(startDate, startTime) : startAt || startDate),
    end: toIsoDateTime(dateOverride ? combineDateTime(endDate, endTime) : endAt || endDate),
    allDay: Boolean(booking.all_day),
    extendedProps: {
      booking: bookingPayload,
      createdBy: booking.created_by || booking.booked_by || booking.user || booking.createdBy,
      created_at: booking.created_at,
      start_at: startAt || `${startDate} ${startTime}`
    }
  };
}

function buildCarEvent(booking, occurrence = {}, dateOverride = '') {
  const startAt = occurrence.start_at || booking.start_at || combineDateTime(booking.start_date, booking.start_time);
  let endAt = occurrence.end_at || booking.end_at || combineDateTime(booking.end_date, booking.end_time);
  const startDate = dateOverride || extractDate(startAt) || booking.start_date;
  let endDate = dateOverride || extractDate(endAt) || booking.end_date;
  const startTime = booking.start_time || extractTime(startAt);
  const endTime = booking.end_time || extractTime(endAt);
  const carTypeName = booking.car_type_name || booking.car_type_value || booking.car_type || 'Car';

  // If allDay or multi-day, increment endDate by 1 day for FullCalendar exclusive end
  let isAllDay = Boolean(booking.all_day);
  let isMultiDay = startDate && endDate && startDate !== endDate;
  let endForCalendar = endAt;
  if (isAllDay || isMultiDay) {
    // Only increment if not already incremented
    const endDateObj = new Date(endDate);
    endDateObj.setDate(endDateObj.getDate() + 1);
    const year = endDateObj.getFullYear();
    const month = String(endDateObj.getMonth() + 1).padStart(2, '0');
    const day = String(endDateObj.getDate()).padStart(2, '0');
    endForCalendar = `${year}-${month}-${day}`;
  }

  const bookingPayload = {
    ...booking,
    start: startDate,
    end: endDate,
    start_at_slot: startTime,
    end_at_slot: endTime,
    bookable: { name: carTypeName },
    car_type_value: booking.car_type_value || booking.car_type,
    car_type_name: booking.car_type_name || carTypeName,
    status: booking.status
  };

  return {
    id: occurrence.id ? `${occurrence.id}-${startDate}` : `${booking.id}-${startDate}`,
    title: booking.title || carTypeName || 'Booking',
    start: toIsoDateTime(dateOverride ? combineDateTime(startDate, startTime) : startAt || startDate),
    end: toIsoDateTime(isAllDay || isMultiDay ? endForCalendar : dateOverride ? combineDateTime(endDate, endTime) : endAt || endDate),
    allDay: isAllDay,
    extendedProps: {
      booking: bookingPayload,
      createdBy: booking.created_by || booking.booked_by || booking.user || booking.createdBy,
      created_at: booking.created_at,
      start_at: startAt || `${startDate} ${startTime}`
    }
  };
}

function combineDateTime(date, time) {
  if (!date) {
    return '';
  }
  if (!time) {
    return date;
  }
  return `${date} ${time}`;
}

function extractDate(dateTime) {
  if (!dateTime) {
    return '';
  }
  return dateTime.split(' ')[0];
}

function extractTime(dateTime) {
  if (!dateTime) {
    return '';
  }
  const parts = dateTime.split(' ');
  return parts[1] ? parts[1].slice(0, 5) : '';
}

function expandDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return startDate ? [startDate] : [];
  }

  if (startDate === endDate) {
    return [startDate];
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [startDate];
  }

  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const day = String(cursor.getDate()).padStart(2, '0');
    days.push(`${year}-${month}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function toIsoDateTime(value) {
  if (!value) {
    return '';
  }
  if (value.includes('T')) {
    return value;
  }
  return value.replace(' ', 'T');
}

function dedupeOccurrencesByDate(occurrences) {
  const seen = new Set();
  const result = [];
  (occurrences || []).forEach((occurrence) => {
    const startAt = occurrence?.start_at || '';
    const date = extractDate(startAt);
    const time = extractTime(startAt);
    const key = `${date}-${time}`;
    if (date && seen.has(key)) {
      return;
    }
    if (date) {
      seen.add(key);
    }
    result.push(occurrence);
  });
  return result;
}

function dedupeRoomEvents(events) {
  const seen = new Set();
  const result = [];
  events.forEach((event) => {
    const bookingId = event?.extendedProps?.booking?.id || event?.id || '';
    const startValue = event?.start || event?.end || '';
    const startDate = typeof startValue === 'string' ? startValue.split('T')[0] : '';
    const key = `${bookingId}-${startDate}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(event);
  });
  return result;
}

function normalizeRoomPayload(payload) {
  const normalized = { ...payload };

  normalized.room_id = payload.room_id || payload.bookable_id;
  delete normalized.bookable_id;

  normalized.all_day = payload.all_day ? 1 : 0;
  if (payload.duration_minutes) {
    normalized.duration_minutes = Number(payload.duration_minutes);
  }

  if (!payload.repeat_type || payload.repeat_type === 'does_not_repeat') {
    delete normalized.repeat_type;
    delete normalized.repeat_interval;
    delete normalized.repeat_on;
    delete normalized.repeat_until;
  } else if (payload.repeat_type === 'daily') {
    normalized.repeat_type = 'daily';
    normalized.repeat_interval = 1;
    normalized.repeat_until = payload.end_date;
  } else if (payload.repeat_type === 'weekly') {
    normalized.repeat_type = 'weekly';
    normalized.repeat_interval = 1;
    normalized.repeat_on =
      Array.isArray(payload.repeat_on) && payload.repeat_on.length ? payload.repeat_on : getWeekdayFromDate(payload.start_date);
    normalized.repeat_until = payload.end_date;
  } else if (payload.repeat_type === 'monthly') {
    normalized.repeat_type = 'monthly';
    normalized.repeat_interval = 1;
    normalized.repeat_on =
      Array.isArray(payload.repeat_on) && payload.repeat_on.length ? payload.repeat_on : getMonthDayFromDate(payload.start_date);
    normalized.repeat_until = payload.end_date;
  }

  normalized.start_date = prepareDateFormat(payload.start_date);
  normalized.end_date = prepareDateFormat(payload.end_date || payload.start_date);
  if (payload.start_time) {
    normalized.start_time = payload.start_time;
  }
  if (payload.end_time) {
    normalized.end_time = payload.end_time;
  }

  delete normalized.repeat_count;
  delete normalized.repeat_unit;
  delete normalized.type;
  delete normalized.room_type;
  delete normalized.status;
  delete normalized.date;
  delete normalized.createdBy;
  delete normalized.created_at;

  return normalized;
}

function normalizeCarPayload(payload) {
  const normalized = { ...payload };

  normalized.car_type = payload.car_type || payload.car_type_value;
  normalized.all_day = payload.all_day ? 1 : 0;

  if (!payload.repeat_type || payload.repeat_type === 'does_not_repeat') {
    delete normalized.repeat_type;
    delete normalized.repeat_interval;
    delete normalized.repeat_on;
    delete normalized.repeat_until;
  } else if (payload.repeat_type === 'daily') {
    normalized.repeat_type = 'daily';
    normalized.repeat_interval = 1;
    normalized.repeat_until = payload.end_date;
  } else if (payload.repeat_type === 'weekly') {
    normalized.repeat_type = 'weekly';
    normalized.repeat_interval = 1;
    normalized.repeat_on =
      Array.isArray(payload.repeat_on) && payload.repeat_on.length ? payload.repeat_on : getWeekdayFromDate(payload.start_date);
    normalized.repeat_until = payload.end_date;
  } else if (payload.repeat_type === 'monthly') {
    normalized.repeat_type = 'monthly';
    normalized.repeat_interval = 1;
    normalized.repeat_on =
      Array.isArray(payload.repeat_on) && payload.repeat_on.length ? payload.repeat_on : getMonthDayFromDate(payload.start_date);
    normalized.repeat_until = payload.end_date;
  }

  normalized.start_date = prepareDateFormat(payload.start_date);
  normalized.end_date = prepareDateFormat(payload.end_date || payload.start_date);
  if (payload.start_time) {
    normalized.start_time = payload.start_time;
  }
  if (payload.end_time) {
    normalized.end_time = payload.end_time;
  }

  delete normalized.repeat_count;
  delete normalized.repeat_unit;
  delete normalized.type;
  delete normalized.room_type;
  delete normalized.status;
  delete normalized.date;
  delete normalized.createdBy;
  delete normalized.created_at;

  return normalized;
}

function extractErrorMessage(error) {
  const responseMessage = error?.response?.data?.message;
  if (responseMessage) {
    return responseMessage;
  }

  const fallbackMessage = error?.message;
  if (fallbackMessage) {
    return fallbackMessage;
  }

  return 'Request failed.';
}

function getWeekdayFromDate(date) {
  const normalizedDate = new Date(date);
  if (Number.isNaN(normalizedDate.getTime())) {
    return [];
  }
  return [normalizedDate.getDay()];
}

function getMonthDayFromDate(date) {
  const normalizedDate = new Date(date);
  if (Number.isNaN(normalizedDate.getTime())) {
    return [];
  }
  return [normalizedDate.getDate()];
}

function getMonthFilter(date) {
  const normalizedDate = new Date(date);
  if (Number.isNaN(normalizedDate.getTime())) {
    return '';
  }

  const year = normalizedDate.getFullYear();
  const month = String(normalizedDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}
